import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, authorizedFetch } from './client';
import { keys } from './keys';

/**
 * Call when the user leaves `/analysis` to remove Analysis report data from the query cache (frees memory).
 * Matches all PO spend-analysis keys (`['po', …]`) and spend-MTO lists/summary (`['spend-mtos', …]`).
 * Next visit runs fresh fetches.
 */
export function clearAnalysisReportsCache(qc: QueryClient) {
  void qc.removeQueries({ queryKey: ['po'] });
  void qc.removeQueries({ queryKey: ['spend-mtos'] });
}

/**
 * Analysis reports: keep server data until mutations invalidate (e.g. PO replace).
 * Avoids refetch when switching browser tabs or report sub-tabs (still cached while hidden).
 */
export const stableReportQueryOptions = {
  staleTime: Infinity,
  gcTime: 1000 * 60 * 60 * 24,
  refetchOnWindowFocus: false,
} as const;

const PoInfoSchema = z.object({
  totalRows: z.coerce.number(),
  itemCount: z.coerce.number(),
  supplierCount: z.coerce.number(),
  dateRange: z
    .object({ min_date: z.string().nullable(), max_date: z.string().nullable() })
    .nullable()
    .optional(),
});

const FiltersSchema = z.object({
  categories: z.array(z.string()),
  suppliers: z.array(z.string()),
});

export function usePoInfo() {
  return useQuery({
    queryKey: keys.poInfo(),
    queryFn: () => apiFetch('/api/po/info', PoInfoSchema),
    ...stableReportQueryOptions,
  });
}

export function usePoFilters() {
  return useQuery({
    queryKey: keys.poFilters(),
    queryFn: () =>
      apiFetch('/api/po/filters', FiltersSchema),
    ...stableReportQueryOptions,
  });
}

export type SpendAnalysisQuery = {
  category?: string;
  supplier?: string;
  search?: string;
  /** Refined PO category → supplier; narrows spend rows before top-impact pivots */
  categorySuppliers?: Record<string, string>;
  /** Include raw grouped rows so the client can pivot without refetch (Top Impact supplier picks). */
  embedPivotSource?: boolean;
};

function spendAnalysisQs(f: SpendAnalysisQuery): string {
  const p = new URLSearchParams();
  if (f.category) p.set('category', f.category);
  if (f.supplier) p.set('supplier', f.supplier);
  if (f.search) p.set('search', f.search);
  if (f.categorySuppliers && Object.keys(f.categorySuppliers).length > 0) {
    p.set('categorySuppliers', JSON.stringify(f.categorySuppliers));
  }
  if (f.embedPivotSource) p.set('embedPivotSource', '1');
  return p.toString();
}

export function useSpendAnalysis(filters: SpendAnalysisQuery) {
  const qs = spendAnalysisQs(filters);
  return useQuery({
    queryKey: keys.spendAnalysis(qs),
    queryFn: () => apiFetch(`/api/po/spend-analysis?${qs}`, z.record(z.string(), z.unknown())),
    ...stableReportQueryOptions,
  });
}

const SpendSummarySchema = z.object({
  builders: z.array(z.record(z.string(), z.unknown())),
  /** Full exec row from API (snake_case); loose schema so extra fields never break parsing */
  programWide: z.any().nullable().optional(),
  latestMonth: z.string().nullable(),
  priorMonth: z.string().nullable(),
  janMonth: z.string().nullable().optional(),
  yearAgoMonth: z.string().nullable().optional(),
});

export function useSpendMtosSummary() {
  return useQuery({
    queryKey: keys.spendSummary(),
    queryFn: () => apiFetch('/api/spend-mtos/summary', SpendSummarySchema),
    ...stableReportQueryOptions,
  });
}

export function useSpendMtos(builderId: string | undefined) {
  return useQuery({
    queryKey: ['spend-mtos', builderId],
    enabled: !!builderId,
    queryFn: () =>
      apiFetch(
        `/api/spend-mtos?builder_id=${encodeURIComponent(builderId!)}`,
        z.array(z.record(z.string(), z.unknown()))
      ),
    ...stableReportQueryOptions,
  });
}

export function usePoUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch('/api/po/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Upload failed');
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.poInfo() });
    },
  });
}

/** Full PO refresh: deletes existing PO rows then imports the CSV (matches legacy /api/po/replace). */
export function usePoReplace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch('/api/po/replace', { method: 'POST', body: fd });
      const data = (await res.json().catch(() => ({}))) as { error?: string; imported?: number };
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.poInfo() });
      void qc.invalidateQueries({ queryKey: keys.poFilters() });
      void qc.invalidateQueries({ queryKey: keys.spendSummary() });
      void qc.invalidateQueries({ queryKey: ['po'] });
    },
  });
}

export function useSpendMtosUploadXls() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ builderId, files }: { builderId: number; files: File[] }) => {
      const fd = new FormData();
      fd.append('builder_id', String(builderId));
      for (const f of files) fd.append('files', f);
      const res = await authorizedFetch('/api/spend-mtos/upload-xls', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Upload failed');
      return data;
    },
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: ['spend-mtos', String(v.builderId)] });
      void qc.invalidateQueries({ queryKey: keys.spendSummary() });
    },
  });
}

export function useSpendMtosUploadCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ builderId, file }: { builderId: number; file: File }) => {
      const fd = new FormData();
      fd.append('builder_id', String(builderId));
      fd.append('file', file);
      const res = await authorizedFetch('/api/spend-mtos/upload-csv', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Upload failed');
      return data;
    },
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: ['spend-mtos', String(v.builderId)] });
      void qc.invalidateQueries({ queryKey: keys.spendSummary() });
    },
  });
}

const CategorySuppliersSchema = z.record(
  z.string(),
  z.array(
    z.object({
      supplier: z.string(),
      count: z.coerce.number().optional(),
      spend: z.unknown().optional(),
    })
  )
);

export function useCategorySuppliers() {
  return useQuery({
    queryKey: ['po', 'category-suppliers'],
    queryFn: () => apiFetch('/api/po/category-suppliers', CategorySuppliersSchema),
    ...stableReportQueryOptions,
  });
}

/** Builder spend-MTO analyze (`POST /api/spend-mtos/analyze`) */
export type SpendAnalyzeCategoryRow = {
  name: string;
  items: number;
  matched: number;
  monthly: Record<string, number>;
  total: number;
};

/** Per-line PO pricing rolled up by calendar month (from `analyzeMtoLineSets`). */
export type SpendAnalyzeLineMonth = {
  avgPrice?: number;
  impactCost?: number;
};

export type SpendAnalyzeLineItem = {
  item?: string;
  qty?: number;
  category?: string | null;
  matched?: boolean;
  monthly?: Record<string, SpendAnalyzeLineMonth | undefined>;
};

export type SpendAnalyzeModel = {
  mtoName?: string;
  spendMtoId?: number | null;
  monthTotals?: Record<string, number>;
  minMonthTotals?: Record<string, number>;
  categoryBreakdown?: SpendAnalyzeCategoryRow[];
  /** Line-level detail for category breakdown UI (same shape as API `items`). */
  items?: SpendAnalyzeLineItem[];
  stats?: { totalItems: number; matchedItems: number; unmatched: number };
};

export type SpendAnalyzeResponse = {
  months?: string[];
  models?: SpendAnalyzeModel[];
};

export function useSpendMtosAnalyze() {
  return useMutation({
    mutationFn: async (vars: { builderId: number; supplierFilters: Record<string, string> }) => {
      const res = await authorizedFetch('/api/spend-mtos/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ builder_id: vars.builderId, supplierFilters: vars.supplierFilters }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Analyze failed');
      return data as SpendAnalyzeResponse;
    },
  });
}

export function useDeleteSpendMto(builderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await authorizedFetch(`/api/spend-mtos/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Delete failed');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['spend-mtos', builderId] });
      void qc.invalidateQueries({ queryKey: keys.spendSummary() });
    },
  });
}

export function usePriceVariance(query: URLSearchParams) {
  const qs = query.toString();
  return useQuery({
    queryKey: keys.priceVariance(qs),
    queryFn: () =>
      apiFetch(`/api/po/price-variance?${qs}`, z.record(z.string(), z.unknown())),
    ...stableReportQueryOptions,
  });
}

const PoItemDetailSchema = z.object({
  item: z.string(),
  rows: z.array(
    z
      .object({
        month: z.string().nullable().optional(),
        supplier: z.string().nullable().optional(),
        qty: z.coerce.number().nullable().optional(),
        spend: z.coerce.number().nullable().optional(),
        avg_price: z.coerce.number().nullable().optional(),
        min_price: z.coerce.number().nullable().optional(),
        max_price: z.coerce.number().nullable().optional(),
        po_count: z.coerce.number().nullable().optional(),
      })
      .passthrough()
  ),
});

export function usePoItemDetail(item: string | null) {
  return useQuery({
    queryKey: ['po', 'item-detail', item ?? ''],
    enabled: !!item,
    queryFn: () =>
      apiFetch(
        `/api/po/item-detail?item=${encodeURIComponent(item!)}`,
        PoItemDetailSchema
      ),
    ...stableReportQueryOptions,
  });
}
